import { addBreadcrumb } from '@/telemetry/sentry';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const BOOT_GRACE_MS = 60_000;
const defaultFetcher: typeof fetch = (input, init) => fetch(input, init);

interface DeploymentVersionOptions {
  fetcher?: typeof fetch;
  hasActiveSession?: () => boolean;
}

export class DeploymentVersion {
  private currentVersion: string | null = null;
  private pendingVersion: string | null = null;
  private pollTimer: number | null = null;
  private bootTimer: number | null = null;
  private initialized = false;
  private fetcher: typeof fetch = defaultFetcher;
  private hasActiveSession = () => false;

  isInitialized(): boolean {
    return this.initialized;
  }

  async initialize(options: DeploymentVersionOptions = {}): Promise<void> {
    this.destroy();
    this.fetcher = options.fetcher ?? defaultFetcher;
    this.hasActiveSession = options.hasActiveSession ?? (() => false);

    try {
      this.currentVersion = await this.fetchVersion();
      if (!this.currentVersion) {
        console.info(
          '[DeploymentVersion] No build-version.txt found, version polling disabled'
        );
        return;
      }

      console.info(
        '[DeploymentVersion] Current build version:',
        this.currentVersion
      );
      this.initialized = true;
      this.bootTimer = window.setTimeout(() => {
        this.startPolling();
      }, BOOT_GRACE_MS);
    } catch (error) {
      console.warn('[DeploymentVersion] Init failed:', getErrorMessage(error));
    }
  }

  destroy(): void {
    if (this.bootTimer !== null) {
      window.clearTimeout(this.bootTimer);
      this.bootTimer = null;
    }
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.initialized = false;
    this.currentVersion = null;
    this.pendingVersion = null;
  }

  get hasPendingUpdate(): boolean {
    return this.pendingVersion !== null;
  }

  reloadPendingUpdate(): void {
    if (!this.pendingVersion) return;
    reloadWithCacheBust();
  }

  private async fetchVersion(): Promise<string | null> {
    try {
      const response = await this.fetcher(
        `/build-version.txt?_=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store',
            Pragma: 'no-cache',
          },
        }
      );
      if (!response.ok) return null;
      const text = (await response.text()).trim();
      // SPA-fallback servers (vite dev, misconfigured Caddy) answer 200
      // with index.html for a missing build-version.txt. Anything that is
      // not a plain version token is "no version" — treating the HTML as a
      // version makes every poll look like a new deploy and reload-loops
      // the kiosk. Mirrors frontend/src/services/DeploymentVersion.js.
      if (!/^[\w.-]{1,64}$/.test(text)) return null;
      return text;
    } catch {
      return null;
    }
  }

  private startPolling(): void {
    if (this.pollTimer !== null) return;

    this.pollTimer = window.setInterval(() => {
      void this.pollOnce();
    }, POLL_INTERVAL_MS);
  }

  private async pollOnce(): Promise<void> {
    if (!(this.initialized && this.currentVersion)) return;

    try {
      const newVersion = await this.fetchVersion();
      if (!newVersion || newVersion === this.currentVersion) return;

      console.warn(
        `[DeploymentVersion] New version detected: ${newVersion} (running: ${this.currentVersion})`
      );
      addBreadcrumb('deployment', `New deployment detected: ${newVersion}`, {
        old_version: this.currentVersion,
        new_version: newVersion,
        has_session: this.hasActiveSession(),
      });

      if (!this.hasActiveSession()) {
        console.warn(
          '[DeploymentVersion] No active session — reloading for new deployment'
        );
        reloadWithCacheBust();
        return;
      }

      this.pendingVersion = newVersion;
      console.info(
        '[DeploymentVersion] Session active — will reload on next session end'
      );
    } catch {
      // Network hiccups must not disrupt kiosk operation.
    }
  }
}

export function reloadWithCacheBust(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

export const deploymentVersion = new DeploymentVersion();
